import { ArrowRight, CalendarClock, ChevronDown, Megaphone, Siren, Wrench } from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/lib/cn'

import type { StatusEvent } from './api'
import { KIND, NOTICE_LEVEL, STATE, STATUS, span, tone, until, when } from './meta'
import { Dot, Link, Pill } from './ui'

const KIND_ICON = { incident: Siren, maintenance: Wrench, notice: Megaphone }

/** The components an event touches, as chips. */
export function Touched({ event }: { event: StatusEvent }) {
  if (!event.components.length) return null
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label="Servizi coinvolti">
      {event.components.map((each) => (
        <li
          key={each.id}
          className="text-fog inline-flex items-center gap-1.5 rounded-md border border-white/[0.07] bg-white/[0.03] px-2 py-0.5 text-[12px]"
        >
          <Dot
            status={event.kind === 'incident' ? each.status : 'maintenance'}
            className="size-2"
          />
          {each.name}
        </li>
      ))}
    </ul>
  )
}

/** An event's updates, newest first, down a line. */
export function Timeline({ event, limit }: { event: StatusEvent; limit?: number }) {
  const [all, setAll] = useState(false)
  const shown = limit && !all ? event.updates.slice(0, limit) : event.updates
  const hidden = event.updates.length - shown.length
  return (
    <div>
      <ol className="relative space-y-5 border-l border-white/10 pl-5">
        {shown.map((update, index) => (
          <li key={update.id} className="relative">
            <span
              className={cn(
                'absolute top-1.5 -left-[25px] size-2.5 rounded-full ring-4 ring-[#0a1020]',
                index === 0 ? STATUS[tone(event)].bg : 'bg-white/25',
              )}
              aria-hidden
            />
            <p className="text-[13px]">
              {event.kind !== 'notice' && (
                <span className="font-semibold text-white">{STATE[update.state]}</span>
              )}
              <span className="text-mist">
                {event.kind !== 'notice' && ' · '}
                <time dateTime={update.at}>{when(update.at)}</time>
              </span>
            </p>
            {update.body && (
              <p className="prose-plain text-fog mt-1 text-[14px] leading-relaxed">{update.body}</p>
            )}
          </li>
        ))}
      </ol>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="text-glow mt-3 inline-flex items-center gap-1 text-[13px] font-medium hover:text-white"
        >
          <ChevronDown className="size-4" aria-hidden />
          {hidden === 1 ? 'Mostra un altro aggiornamento' : `Mostra altri ${hidden} aggiornamenti`}
        </button>
      )}
    </div>
  )
}

/** An open incident, a maintenance under way or planned, a notice. */
export function EventCard({ event }: { event: StatusEvent }) {
  const look = STATUS[tone(event)]
  const Icon = KIND_ICON[event.kind]
  const scheduled = event.state === 'scheduled'
  return (
    <article
      className="card animate-rise relative overflow-hidden"
      style={{ borderColor: `color-mix(in srgb, ${look.hex} 30%, transparent)` }}
    >
      <span className={cn('absolute inset-y-0 left-0 w-1', look.bg)} aria-hidden />
      <div className="p-5 pl-6 sm:p-6 sm:pl-7">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span
              className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-xl"
              style={{ background: `color-mix(in srgb, ${look.hex} 16%, transparent)` }}
            >
              <Icon className={cn('size-[18px]', look.text)} strokeWidth={2.2} aria-hidden />
            </span>
            <div className="min-w-0">
              <p className={cn('text-[11px] font-semibold tracking-[0.18em] uppercase', look.text)}>
                {event.kind === 'notice' ? NOTICE_LEVEL[event.impact] : KIND[event.kind]}
              </p>
              <h3 className="mt-0.5 text-[16px] leading-snug font-semibold text-balance text-white">
                <Link
                  to={`/eventi/${event.id}`}
                  className="hover:underline hover:underline-offset-4"
                >
                  {event.title}
                </Link>
              </h3>
            </div>
          </div>
          {event.kind !== 'notice' && (
            <Pill status={tone(event)}>
              <Dot status={tone(event)} pulse={!scheduled} className="size-2" />
              {STATE[event.state]}
            </Pill>
          )}
        </header>

        {event.kind === 'maintenance' && event.ends_at && (
          <p className="text-fog mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
            <CalendarClock className="text-maint size-4" aria-hidden />
            <span>
              {when(event.starts_at)} → {when(event.ends_at)}
            </span>
            <span className="text-mist">
              ·{' '}
              {scheduled
                ? `inizia ${until(event.starts_at)}`
                : `durata prevista ${span(event.starts_at, event.ends_at)}`}
            </span>
          </p>
        )}
        {event.kind === 'incident' && (
          <p className="text-mist mt-4 text-[13px]">
            Iniziato {when(event.starts_at)} · in corso da {span(event.starts_at, null)}
          </p>
        )}

        <div className="mt-4 space-y-4">
          <Touched event={event} />
          {event.kind === 'notice' ? (
            // A notice's words are its first update's: what staff published.
            event.updates.at(-1)?.body && (
              <p className="prose-plain text-fog text-[14px] leading-relaxed">
                {event.updates.at(-1)?.body}
              </p>
            )
          ) : (
            <Timeline event={event} limit={scheduled ? 1 : 3} />
          )}
        </div>

        <Link
          to={`/eventi/${event.id}`}
          className="text-glow mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium hover:text-white"
        >
          Dettagli
          <ArrowRight className="size-3.5" aria-hidden />
        </Link>
      </div>
    </article>
  )
}

/** A closed event, as one line of the past. */
export function PastRow({ event }: { event: StatusEvent }) {
  const look =
    STATUS[
      event.kind === 'maintenance' ? 'maintenance' : tone({ ...event, state: 'investigating' })
    ]
  const last = event.updates[0]
  return (
    <li>
      <Link
        to={`/eventi/${event.id}`}
        className="group -mx-3 flex items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-white/[0.03]"
      >
        <span className={cn('mt-1.5 size-2.5 shrink-0 rounded-full', look.bg)} aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-semibold text-white group-hover:underline group-hover:underline-offset-4">
            {event.title}
          </span>
          {last?.body && (
            <span className="text-mist mt-0.5 line-clamp-2 block text-[13px]">{last.body}</span>
          )}
          <span className="text-mist/80 mt-1 block text-[12px]">
            {KIND[event.kind]} · {when(event.starts_at)}
            {event.closed_at && <> · durata {span(event.starts_at, event.closed_at)}</>}
          </span>
        </span>
      </Link>
    </li>
  )
}
